import { ComponentType, PropsWithChildren, ReactNode } from 'react';

import {
  Extension,
  attachComponentData,
  useElementFilter,
} from '@backstage/core-plugin-api';

export type ItemRenderExtensionOptions<TItem> = {
  /** unique identifier for the extension */
  id: string;

  /** priority of the extension - default is 0 */
  priority?: number;

  /** predicate to determine if the extension should be used for the given item */
  predicate: (item: TItem) => boolean;

  /** function to render the item */
  render: (item: TItem) => ReactNode;
};

// Extension keys for component data attachment
const ITEM_RENDER_EXTENSION_KEY = 'confidence.item-render-extension';
const ITEM_RENDER_EXTENSION_WRAPPER_KEY =
  'confidence.item-render-extension-wrapper';

export type ItemRenderExtensionComponent<_TReturnValue, _TInputProps> =
  () => null;

/**
 * Creates an item render extension that can be registered with a category wrapper
 */
export const createItemRenderExtension = <
  TOptions extends ItemRenderExtensionOptions<any>,
  TReturnValue = unknown,
  TInputProps = unknown,
>(
  options: TOptions,
): Extension<ItemRenderExtensionComponent<TReturnValue, TInputProps>> => {
  return {
    expose() {
      const ActivityItemExtensionDataHolder = () => null;

      attachComponentData(
        ActivityItemExtensionDataHolder,
        ITEM_RENDER_EXTENSION_KEY,
        options,
      );

      return ActivityItemExtensionDataHolder;
    },
  };
};

/**
 * Creates a category wrapper component for item render extensions
 *
 * @param options - options for the category wrapper
 * @param options.id - unique identifier for the category wrapper
 * @returns the category wrapper component
 *
 * @example
 * ```tsx
 * const ItemRenderExtensions = createItemRenderExtensions({
 *   id: 'item-render',
 * });
 *
 * <ItemRenderExtensions>
 *   // ...extensions
 * </ItemRenderExtensions>
 * ```
 */
export const createItemRenderExtensions = <
  TOptions extends ItemRenderExtensionOptions<any>,
>(options: {
  id: string;
}): ComponentType<PropsWithChildren<{}>> => {
  const ItemRenderExtensionsWrapper: ComponentType<PropsWithChildren<{}>> = ({
    children,
  }): JSX.Element | null => {
    // Extract extensions from children
    const extensions = useElementFilter(children, elements =>
      elements.findComponentData<TOptions>({
        key: ITEM_RENDER_EXTENSION_KEY,
      }),
    );

    // Store extensions on the wrapper component
    (ItemRenderExtensionsWrapper as any).__extensions__ = extensions;

    return null;
  };

  // Use a unique wrapper key for each category to enable filtering
  const categoryWrapperKey = `${ITEM_RENDER_EXTENSION_WRAPPER_KEY}.${options.id}`;

  attachComponentData(ItemRenderExtensionsWrapper, categoryWrapperKey, true);

  return ItemRenderExtensionsWrapper;
};

/**
 * Hook to retrieve all registered item render extensions for a specific category
 */
export const useItemRenderExtensions = <
  TOptions extends ItemRenderExtensionOptions<any>,
  TComponentDataType = TOptions,
>(
  wrapper: ComponentType<PropsWithChildren<{}>>,
): TComponentDataType[] => {
  // Get the stored extensions from the wrapper component
  return (wrapper as any).__extensions__ || [];
};
